import { createFileRoute, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Section, SectionHeader } from "@/components/site/Section";
import { BehaviorQuestionnaire } from "@/components/site/BehaviorQuestionnaire";
import {
  getRaterContext,
  saveRaterProgress,
  submitRater,
  type RaterContext,
} from "@/utils/review.functions";
import type { Answers } from "@/content/review";

export const Route = createFileRoute("/review/$token")({
  head: () => ({
    meta: [
      { title: "Give leadership feedback | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Answer 16 short questions about how someone leads. Takes about five minutes, no account needed.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Give leadership feedback" },
      { property: "og:description", content: "Sixteen short questions about how someone leads." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RaterPage,
});

function Shell({ title, body }: { title: string; body: string }) {
  return (
    <Section>
      <SectionHeader
        title={title}
        intro={body}
        titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
      />
    </Section>
  );
}

function RaterPage() {
  const { token } = useParams({ from: "/review/$token" });
  const loadContext = useServerFn(getRaterContext);
  const save = useServerFn(saveRaterProgress);
  const send = useServerFn(submitRater);

  const [context, setContext] = useState<RaterContext | null>(null);
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    loadContext({ data: { token } })
      .then((result) => {
        if (active) setContext(result);
      })
      .catch(() => {
        if (active) setError("We couldn't open this link.");
      });
    return () => {
      active = false;
    };
  }, [loadContext, token]);

  if (error) return <Shell title="Something went wrong" body={error} />;
  if (!context) return <Shell title="Opening your feedback form" body="One moment." />;
  if (!context.found)
    return (
      <Shell
        title="This link isn't valid"
        body="It may have been reissued. Ask the person who sent it for a fresh link."
      />
    );
  if (done || context.submitted)
    return (
      <Shell
        title="Thank you, that's everything"
        body="Your answers are in. This link is now read-only and your individual responses are never shown."
      />
    );
  if (context.closed)
    return (
      <Shell
        title="This feedback round is closed"
        body="The leader has closed their check, so answers are no longer being collected."
      />
    );

  const leader = context.leaderName || "this leader";
  const relationship = context.relationship ?? "peer";
  const identified = relationship === "manager";

  if (!started) {
    return (
      <Section>
        <div className="mx-auto max-w-2xl">
          <SectionHeader
            eyebrow="360 Leader Behavior Check"
            title={
              relationship === "self" ? "Rate how you lead" : `Your view of how ${leader} leads`
            }
            intro="Sixteen short questions, about five minutes. There is no account to create."
            titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          />
          <div className="rounded-2xl border border-primary/20 bg-lavender p-7 md:p-9">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Before you start
            </p>
            <h3 className="mt-2 font-display text-2xl font-semibold text-ink">
              {identified
                ? "Your rating is shown separately and is identified"
                : "Your answers are pooled with your group"}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {identified
                ? `You are answering as ${leader}'s manager. Because that is one person, your rating appears on its own labeled line. ${leader} will know it came from you.`
                : `Your answers are averaged with everyone else in your group and shown only once three people have answered. ${leader} never sees individual responses or who said what.`}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Rate what actually happens, not what you think is intended. If you don't have enough
              visibility on something, say so instead of guessing.
            </p>
            <button
              type="button"
              onClick={() => setStarted(true)}
              className="mt-6 inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand"
            >
              Start the 16 questions
            </button>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-lavender py-16">
      <BehaviorQuestionnaire
        relationship={relationship}
        initialAnswers={context.answers}
        submitting={busy}
        submitLabel="Submit my feedback"
        onSaveProgress={(answers: Answers) => {
          void save({ data: { token, answers } }).catch(() => undefined);
        }}
        onSubmit={(answers: Answers) => {
          setBusy(true);
          send({ data: { token, answers } })
            .then(() => setDone(true))
            .catch(() => setError("We couldn't save your answers. Please try again."))
            .finally(() => setBusy(false));
        }}
      />
    </div>
  );
}
